import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { StorageKeys } from 'shared/lib/storage/storageService';

/**
 * Production keys are issued in the background once KYC passes or the first invoice is paid, and
 * nothing pushes that to the dashboard. Those submissions open a short window during which the key
 * queries poll, so the production switch unlocks without a page reload.
 *
 * Persisted because the user can reload or navigate away while the key is still being issued.
 */
const WATCH_WINDOW_MS = 5 * 60 * 1000;

export const PROD_KEY_WATCH_REFETCH_INTERVAL = 15000; // 15 seconds

type ProdKeyWatchState = {
  /** ISO timestamp until which the key queries keep polling. */
  watchUntil: string | null;
};

type ProdKeyWatchActions = {
  startWatching: () => void;
  /** Read at call time rather than subscribed, so the deadline does not need a re-render to apply. */
  isWatching: () => boolean;
};

export const useProdKeyWatchStore = create<ProdKeyWatchState & ProdKeyWatchActions>()(
  persist(
    (set, get) => ({
      watchUntil: null,
      startWatching: () => set({ watchUntil: new Date(Date.now() + WATCH_WINDOW_MS).toISOString() }),
      isWatching: () => {
        const { watchUntil } = get();

        return !!watchUntil && new Date(watchUntil).getTime() > Date.now();
      },
    }),
    {
      name: StorageKeys.PROD_KEY_WATCH_STATE,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ watchUntil: state.watchUntil }),
    },
  ),
);
